import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import { CreditCard, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { changeOperationStatus } from '../utils/statusChangeHelper';

const PaiementModal = ({ open, onClose, operation, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [transactionReference, setTransactionReference] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');

  const paymentMethods = [
    { value: 'especes', label: 'Espèces' },
    { value: 'virement', label: 'Virement bancaire' },
    { value: 'cheque', label: 'Chèque' },
    { value: 'mobile_money', label: 'Mobile Money' },
    { value: 'carte', label: 'Carte bancaire' },
    { value: 'autre', label: 'Autre' }
  ];

  const validateForm = () => {
    if (!paymentMethod) {
      toast({
        title: "Champ manquant",
        description: "Le mode de paiement est obligatoire",
        variant: "destructive"
      });
      return false;
    }

    if (!paymentDate) {
      toast({
        title: "Champ manquant",
        description: "La date de paiement est obligatoire",
        variant: "destructive"
      });
      return false;
    }

    return true;
  };

  const handleConfirm = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      // Change status to payee
      await changeOperationStatus(operation.id, 'payee', {
        payment_method: paymentMethod,
        transaction_reference: transactionReference,
        payment_date: paymentDate,
        payment_notes: notes
      });

      toast({
        title: "✅ Paiement confirmé",
        description: "Le paiement a été enregistré avec succès"
      });

      if (onSuccess) {
        onSuccess();
      }

      onClose();

    } catch (error) {
      console.error('Error confirming payment:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible de confirmer le paiement",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (!operation) return null;

  const invoiceAmount = operation.quote_details?.total_ttc || operation.estimatedPrice || 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <CreditCard className="w-6 h-6 text-emerald-600" />
            Confirmer la réception du paiement
          </DialogTitle>
          <DialogDescription>
            Opération #{operation.operationNumber || operation.id?.slice(0, 8)}
          </DialogDescription>
        </DialogHeader>

        {/* Invoice Summary */}
        <Alert className="bg-emerald-50 border-emerald-200">
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          <AlertDescription>
            <div className="flex items-center justify-between">
              <span className="font-semibold">Montant de la facture :</span>
              <span className="text-2xl font-bold text-emerald-600">
                {invoiceAmount.toLocaleString('fr-FR')} FCFA
              </span>
            </div>
          </AlertDescription>
        </Alert>

        <div className="space-y-4">
          {/* Payment Method */}
          <div>
            <Label htmlFor="paymentMethod">
              Mode de paiement <span className="text-red-500">*</span>
            </Label>
            <Select value={paymentMethod} onValueChange={setPaymentMethod}>
              <SelectTrigger id="paymentMethod">
                <SelectValue placeholder="Sélectionner un mode de paiement" />
              </SelectTrigger>
              <SelectContent>
                {paymentMethods.map((method) => (
                  <SelectItem key={method.value} value={method.value}>
                    {method.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Transaction Reference */}
          <div>
            <Label htmlFor="transactionReference">
              Référence de transaction (optionnel)
            </Label>
            <Input
              id="transactionReference"
              type="text"
              value={transactionReference}
              onChange={(e) => setTransactionReference(e.target.value)}
              placeholder="Ex: TXN123456789"
            />
            <p className="text-xs text-gray-500 mt-1">
              Numéro de chèque, référence virement, ID transaction, etc.
            </p>
          </div>

          {/* Payment Date */}
          <div>
            <Label htmlFor="paymentDate">
              Date de paiement <span className="text-red-500">*</span>
            </Label>
            <Input
              id="paymentDate"
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
            />
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes">Notes (optionnel)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Informations complémentaires sur le paiement..."
              rows={3}
            />
          </div>
        </div>

        {/* Warning */}
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Important :</strong> En confirmant ce paiement, vous certifiez que les fonds ont bien été reçus et vérifiés.
          </AlertDescription>
        </Alert>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Annuler
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={loading}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {loading ? 'Confirmation...' : 'Confirmer le paiement'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PaiementModal;
